import { useMemo, useState, useEffect, useId } from "react";
import styled from "styled-components";
import { UserRowData } from "../../../types/general-types";
import "./SelectionMenu.css";
import { SessionInfo, LiveSessionNote, FlagLevels } from "../TestResultsWrapper";
import {
	CoreButton,
	CoreCard,
	CoreSearchField,
	CoreSearchFieldPaper,
	CoreTable,
	CoreTableColumn,
	CoreTableRow,
} from "@verity/components/core";
import i18n from "../../../i18n";
import StudentTableIcon from "../../../images/reports/studentTable.svg?react";
import { FlagLevelBadge } from "../../../reports/shared/report-components";

// Styled Severe Flag Level Display
export const SevereFlagLevel = styled.span`
	background-color: #f8dcdb;
	color: #344054;
	border-width: 0;
	border-radius: 4px;
	height: 28px;
	width: 70px;
	text-wrap: nowrap;
	display: flex;
	justify-content: center;
	align-items: center;
`;

// Styled Severe Flag Level Display
export const MajorFlagLevel = styled(SevereFlagLevel)`
	background-color: #faefde;
`;

// Styled Severe Flag Level Display
export const MinorFlagLevel = styled(SevereFlagLevel)`
	background-color: #dbe7e1;
`;

export const PendingFlagLevel = styled(SevereFlagLevel)`
	background-color: #e6e6e6;
`;

export const NoneFlagLevel = styled(SevereFlagLevel)`
	background-color: #e6e6e6;
`;

export const SelectionMenu = (props: {
	courseId: number;
	userIdLiveNotesMap: Map<LiveSessionNote["student"], LiveSessionNote[]> | null;
	userIdProctoringSessionMap: Map<SessionInfo["studentId"], SessionInfo[]> | null;
	requireDownload: boolean;
	requireAut: boolean;
	flagLevelFilter: FlagLevels;
	setSelectedStudent: (studentId: number) => void;
	setShowStudentResults: (show: boolean) => void;
	setTableData: (tableData: UserRowData[]) => void;
}) => {
	const {
		userIdProctoringSessionMap,
		requireDownload,
		requireAut,
		flagLevelFilter: originalFlagLevelFilter,
		setSelectedStudent,
		setShowStudentResults,
		setTableData,
	} = props;
	const [studentNameFilter, setStudentNameFilter] = useState("");
	const [flagLevelFilter, setFlagLevelFilter] = useState<FlagLevels>(originalFlagLevelFilter);
	const controlId = useId();

	const navigateToStudentResults = (studentId: number) => {
		const url = new URL(window.location.href);
		url.searchParams.set("userId", studentId.toString());
		window.history.replaceState(window.history.state, "", url);
		setSelectedStudent(studentId);
		setShowStudentResults(true);
	};

	useEffect(() => {
		if (originalFlagLevelFilter !== flagLevelFilter) {
			setFlagLevelFilter(originalFlagLevelFilter);
		}
	}, [flagLevelFilter, originalFlagLevelFilter, props.flagLevelFilter]);

	const columns: CoreTableColumn<UserRowData>[] = [
		{
			key: "student_name",
			header: i18n.t("result.components.studentSelection.selectionMenuTSX.testTaker"),
			truncateString: true,
			cell: (row) => (row.data.student_name ? row.data.student_name : "Unknown Test-Taker"),
			descendingComparator: (a, b) => {
				if (!a || !b) return 0;
				if (!a.student_name || a.student_name === "") return -1;
				if (!b.student_name || b.student_name === "") return 1;
				if (a.student_name?.toLowerCase() > b.student_name?.toLowerCase()) {
					return -1;
				} else if (a.student_name?.toLowerCase() < b.student_name?.toLowerCase()) {
					return 1;
				}
				return 0;
			},
		},
		{
			key: "attempts",
			header: i18n.t("result.components.studentSelection.selectionMenuTSX.attemptsCount"),
			cell: (row) => row.data.attempts,
			descendingComparator: (a, b) => {
				if (!a || !b) return 0;
				if (a.attempts > b.attempts) {
					return -1;
				} else if (a.attempts < b.attempts) {
					return 1;
				}
				return 0;
			},
		},
		{
			key: "status",
			header: i18n.t("result.components.studentSelection.selectionMenuTSX.status"),
			cell: (row) =>
				row.data.status ? row.data.status : i18n.t("result.components.studentSelection.selectionMenuTSX.unavailable"),
			descendingComparator: (a, b) => {
				if (!a || !b) return 0;
				if (!a.status) return -1;
				if (!b.status) return 1;
				const statusOrder = ["Complete", "Incomplete", "Unavailable", "Pending", "Error"];
				const aIndex = statusOrder.indexOf(a.status);
				const bIndex = statusOrder.indexOf(b.status);
				if (aIndex > bIndex) {
					return -1;
				} else if (aIndex < bIndex) {
					return 1;
				}
				return 0;
			},
		},
		{
			key: "start_date",
			header: i18n.t("result.components.studentSelection.selectionMenuTSX.startDate"),
			cell: (row) => {
				if (row.data.first_attempt === null || row.data.first_attempt === undefined) {
					return;
				}
				const date = new Date(row.data.first_attempt);
				return date.getMonth() + 1 + "/" + date.getDate() + "/" + date.getFullYear();
			},
			descendingComparator: (a, b) => {
				if (!a || !b) return 0;
				if (new Date(a.first_attempt).getTime() > new Date(b.first_attempt).getTime()) {
					return -1;
				} else if (new Date(a.first_attempt).getTime() < new Date(b.first_attempt).getTime()) {
					return 1;
				}
				return 0;
			},
		},
		{
			key: "flag_level",
			header: i18n.t("result.components.studentSelection.selectionMenuTSX.flagLvl"),
			cell: (row) => <FlagLevelBadge flagLevel={row.data.flag_level} />,
			descendingComparator: (a, b) => {
				if (!a || !b) return 0;
				if (a.flag_level > b.flag_level) {
					return -1;
				} else if (a.flag_level < b.flag_level) {
					return 1;
				}
				return 0;
			},
			width: "12%",
		},
		{
			header: i18n.t("result.components.studentSelection.selectionMenuTSX.viewResults"),
			key: "view_results",
			cell: () => <CoreButton>{i18n.t("result.components.studentSelection.selectionMenuTSX.viewResults")}</CoreButton>,
			align: "left",
		},
	];

	// useMemo to memoize the data so that it persists between rerenders of the table.
	const data = useMemo(() => {
		const formattedData: UserRowData[] = [];
		// Iterator to loop through all the keys of the map. Each key corresponds with an array
		const userIdIterator = userIdProctoringSessionMap?.keys();
		if (!userIdIterator) {
			return null;
		}

		const getAverageDuration = (sessionList: SessionInfo[] | undefined): string => {
			if (!sessionList) {
				return "";
			}

			let totalDuration = 0;
			let totalSessions = 0;
			for (let i = 0; i < sessionList?.length; i++) {
				const { startTime, endTime } = sessionList[i];
				if (startTime && endTime) {
					try {
						const sDate = new Date(startTime);
						const eDate = new Date(endTime);
						totalDuration += eDate.getTime() - sDate.getTime();
						totalSessions++;
					} catch {
						continue;
					}
				}
			}
			const avgDurMs = totalDuration / totalSessions;

			if (avgDurMs > 0) {
				if (avgDurMs < 60 * 1000) {
					// Less than 1 minute
					const seconds = Math.round(avgDurMs / 1000);
					return i18n.t("result.components.studentSelection.selectionMenuTSX.averageDuration.seconds", {
						count: seconds,
					});
				} else if (avgDurMs < 60 * 60 * 1000) {
					// Less than 1 hour
					const minutes = parseFloat((avgDurMs / (60 * 1000)).toFixed(1));
					return i18n.t("result.components.studentSelection.selectionMenuTSX.averageDuration.minutes", {
						count: minutes,
					});
				} else {
					// 1 hour or more
					const hours = parseFloat((avgDurMs / (60 * 60 * 1000)).toFixed(1));
					return i18n.t("result.components.studentSelection.selectionMenuTSX.averageDuration.hours", { count: hours });
				}
			} else {
				return "";
			}
		};

		for (const userId of userIdIterator) {
			const info = userIdProctoringSessionMap?.get(userId)?.[0]["userInfo"];
			let maxFlagLevel = 0;
			const totalFlagLevels: { [key: number]: number } = {};
			for (let i = 0; i < (userIdProctoringSessionMap?.get(userId)?.length ?? 0); i++) {
				const session = userIdProctoringSessionMap?.get(userId)?.[i];
				if (typeof session?.["flagLevel"] === "number") {
					if ((session["flagLevel"] ?? 0) > maxFlagLevel) {
						maxFlagLevel = session["flagLevel"];
					}
					totalFlagLevels[session.flagLevel ?? 0] = (totalFlagLevels[session.flagLevel ?? 0] ?? 0) + 1;
				}
			}

			const proctoringSessions = userIdProctoringSessionMap?.get(userId);
			const status = proctoringSessions?.some((x) => x.status === "unflagged" || x.status === "flagged")
				? "Complete"
				: proctoringSessions?.some((x) => x.status === "incomplete")
					? "Incomplete"
					: proctoringSessions?.some((x) => x.status === "unavailable")
						? "Unavailable"
						: proctoringSessions?.some((x) => x.status === "pending")
							? "Pending"
							: "Error";
			formattedData.push({
				userId: userId,
				profile_picture: info?.profile_picture ?? undefined,
				student_name: info?.full_name ?? info?.username ?? undefined, // Use the username if the full_name is not available
				attempts: userIdProctoringSessionMap?.get(userId)?.length ?? 0,
				flag_level: maxFlagLevel,
				anomalies_count: totalFlagLevels[maxFlagLevel],
				// The toLocaleString function converts the date into the string format we want
				first_attempt: proctoringSessions?.[0]?.startTime
					? new Date(proctoringSessions?.[0]?.startTime).toString()
					: "",
				last_attempt: proctoringSessions?.length
					? new Date(proctoringSessions[proctoringSessions.length - 1]?.startTime ?? "").toString()
					: "",
				avgDuration: getAverageDuration(proctoringSessions),
				status: status,
			});
		}
		setTableData(formattedData);
		return formattedData;
	}, [setTableData, userIdProctoringSessionMap]);

	const filteredData = useMemo(() => {
		const applyFlagFilter = (flagLevel: number) => {
			switch (flagLevel) {
				case 1:
					return flagLevelFilter.minor;
				case 2:
					return flagLevelFilter.major;
				case 3:
					return flagLevelFilter.severe;
				default:
					return false;
			}
		};

		return data?.filter((x) => {
			const studentNameMatch =
				studentNameFilter === "" ? true : x.student_name?.toLowerCase()?.includes(studentNameFilter.toLowerCase());
			const flagLevelMatch = flagLevelFilter.all ? true : applyFlagFilter(x.flag_level);
			return studentNameMatch && flagLevelMatch;
		});
	}, [data, flagLevelFilter, studentNameFilter]);

	// useCallback to memoize the row subcomponent so that it will only change when the props passed in change

	return (
		<CoreCard
			icon={StudentTableIcon}
			useHeader
			title={i18n.t("result.components.studentSelection.selectionMenuTSX.testTakerAnalytics")}
			style={{ minHeight: "600px" }}
		>
			<div
				style={{
					fontSize: "15px",
					fontWeight: 400,
					lineHeight: "17.9px",
					textAlign: "left",
					color: "#6B6B6B",
					marginTop: "10px",
					marginBottom: "20px",
				}}
			>
				{i18n.t("result.components.studentSelection.selectionMenuTSX.viewAssessmentAttempts")}
			</div>
			<CoreSearchFieldPaper sx={{ marginBottom: "20px" }}>
				<CoreSearchField
					onSearch={setStudentNameFilter}
					label={i18n.t("result.components.studentSelection.selectionMenuTSX.searchByTestTakers")}
					totalResults={filteredData?.length ?? 0}
					ariaControls={controlId}
					showSearchButton
					sx={{
						flexGrow: "0.7",
					}}
					value={studentNameFilter}
				/>
			</CoreSearchFieldPaper>
			<CoreTable
				id={controlId}
				columns={columns}
				data={filteredData ?? []}
				loading={data === null && !(requireDownload || requireAut)}
				noResults={requireDownload || requireAut}
				noResultsString={
					requireDownload
						? i18n.t("result.components.studentSelection.selectionMenuTSX.needExtension")
						: requireAut
							? i18n.t("result.components.studentSelection.selectionMenuTSX.authUser")
							: i18n.t("result.components.studentSelection.selectionMenuTSX.noResult")
				}
				paginationSize="large"
				noSubComponentPadding
				resetPageOnDataChange
				onRowClick={(row: CoreTableRow<UserRowData>) => {
					navigateToStudentResults(row.data.userId);
				}}
			/>
		</CoreCard>
	);
};

export default SelectionMenu;
